import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import {
  AppButton,
  Box,
  ErrorModal,
  Inscription,
  RoundCloseIcon,
  useResponsive,
  WarningLabel,
} from '@common-fe/common-fe';

import { SuccessModal } from '@/components/elements';
import useAddPlaidPaymentMethod from '@/modules/transaction/components/ReimburseMe/queries/useAddPlaidPaymentMethod.query';
import { PaymentMethodVerificationStatus } from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { usePlaid } from '@/utils/plaid/hooks/usePlaid';
import PlaidModal from '@/utils/plaid/PlaidModal';
import useSubmitPlaidBankAccountActivationQuery from '@/utils/plaid/queries/useSubmitPlaidBankAccountActivation.query';
import { useDuplicatePlaidAccountStore } from '@/utils/plaid/store';

interface Props {
  status?: PaymentMethodVerificationStatus;
  withoutButton?: boolean;
  isHighlighted?: boolean;
  highlightedTop?: boolean;
  accountId?: string;
  refetch?: () => void;
  additionalOnCLickAction?: () => void;
  onlyOpenPlaidModeId?: string;
}

export const PlaidDirectDepositBanner: React.FC<Props> = ({
  status,
  withoutButton,
  isHighlighted,
  highlightedTop,
  accountId,
  refetch,
  additionalOnCLickAction,
  onlyOpenPlaidModeId,
}) => {
  const { isMobile } = useResponsive();
  const [isPlaidLoading, setIsPlaidLoading] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  const { t } = useTranslation();
  const { updateBankStatus } = useSubmitPlaidBankAccountActivationQuery();
  const setDuplicateAccountModal = useDuplicatePlaidAccountStore((state) => state.setIsDuplicateModalVisible);

  const errorStatuses = useMemo(
    () =>
      status === PaymentMethodVerificationStatus.VERIFICATION_FAILED ||
      status === PaymentMethodVerificationStatus.EXPIRED,
    [status]
  );
  const pendingStatuses = useMemo(
    () =>
      status === PaymentMethodVerificationStatus.PENDING ||
      status === PaymentMethodVerificationStatus.PENDING_MANUAL,
    [status]
  );
  const handleUpdateBankStatus = useCallback(async () => {
    try {
      setIsPlaidLoading(true);
      await updateBankStatus({ elevate_account_id: accountId as string });
      setSuccessModal(true);
      setIsPlaidLoading(false);
    } catch (e) {
      // @ts-ignore
      if (e.response.status === 409) {
        setDuplicateAccountModal(true);
        setIsPlaidLoading(false);
        return;
      }
      setIsPlaidLoading(false);
      setErrorModal(true);
    }
  }, [accountId, updateBankStatus, setDuplicateAccountModal]);
  // const handleSaveSuccess = useCallback(() => {
  //   setSuccessModal(true);
  // }, []);
  const { save: plaidSave } = useAddPlaidPaymentMethod();
  const handlePlaidPaymentMethodTryAgain = useCallback(
    async (plaidMetadata: PlaidLinkOnSuccessMetadata) => {
      try {
        setIsPlaidLoading(true);
        await plaidSave({
          plaidMetadata,
          accountId,
          isTryAgainFlow: true,
        });
        setIsPlaidLoading(false);
        const currentAccountStatus = plaidMetadata?.accounts[0]?.verification_status;
        if (!currentAccountStatus) {
          setSuccessModal(true);
        }
      } catch (e) {
        // @ts-ignore
        if (e.response.status === 409) {
          setDuplicateAccountModal(true);
          setIsPlaidLoading(false);
          return;
        }
        setErrorModal(true);
        setIsPlaidLoading(false);
      }
    },
    [accountId, plaidSave, setDuplicateAccountModal]
  );

  const { openPlaid, content } = usePlaid({
    accountId: pendingStatuses ? accountId : undefined,
    onSuccess: pendingStatuses ? handleUpdateBankStatus : handlePlaidPaymentMethodTryAgain,
    onExit: refetch,
    isManualVerification: pendingStatuses,
    isTryAgainFlow: errorStatuses,
  });

  useEffect(() => {
    if (onlyOpenPlaidModeId) {
      openPlaid();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyOpenPlaidModeId]);

  if (!status) {
    return null;
  }

  if (pendingStatuses) {
    return (
      <>
        {highlightedTop && !onlyOpenPlaidModeId ? (
          <Box
            height="2px"
            width="calc(100% - 48px)"
            background={{ color: 'warning' }}
            margin={{ bottom: '-2px', horizontal: 'spacing24' }}
            style={{ zIndex: 1 }}
          />
        ) : null}

        {onlyOpenPlaidModeId ? null : (
          <Box
            data-testid="PlaidDirectDepositBanner"
            pad={{
              horizontal: 'spacing24',
              vertical: 'spacing16',
            }}
            background={{ color: 'warningContainer' }}
            border={{
              size: isHighlighted ? '2px' : '1px',
              color: isHighlighted ? 'warning' : 'warningBorder',
            }}
            round="fieldRound"
            direction={isMobile ? 'column' : 'row'}
            justify="between"
            align={isMobile ? 'start' : 'center'}
          >
            <Box>
              <Inscription weight="bold" margin={{ bottom: 'spacing8' }} color="textBody">
                <WarningLabel>{t('Your account verification is in progress.')}</WarningLabel>
              </Inscription>

              <Box>
                <Inscription margin={{ bottom: 'spacing8' }} color="textBody" size="14px">
                  {t('A small deposit will be posted to your account within one to two business days. ')}
                  {status === PaymentMethodVerificationStatus.PENDING
                    ? t('Once posted, your account will automatically verified. No further action is needed.')
                    : t('Once posted, you will need to provide additional details to verify your account.')}
                </Inscription>
              </Box>
            </Box>
            {status === PaymentMethodVerificationStatus.PENDING_MANUAL && !withoutButton && (
              <Box margin={isMobile ? {} : { left: 'spacing24', top: 'spacing24' }}>
                <AppButton fill={isMobile} onClick={openPlaid} testId="plaid-action" width="140px">
                  {t('Verify Account')}
                </AppButton>
              </Box>
            )}
            {content}
          </Box>
        )}

        {isPlaidLoading && <PlaidModal visible isLoading />}
        <SuccessModal
          header={t('Your account has been successfully verified')}
          buttonText={t('Got It!')}
          visible={successModal}
          onSetVisible={() => {
            setSuccessModal(false);
            if (refetch) {
              refetch();
            }
          }}
          buttonStyle={{ width: '160px' }}
        />
        <ErrorModal
          testId="plaid-banner-error-modal"
          visible={errorModal}
          onSetVisible={() => {
            setErrorModal(false);
          }}
          header={t('Something went wrong')}
          helpText={t('Try again later')}
          buttonText={t('Got It!')}
          onSubmit={() => {
            setErrorModal(false);
          }}
        />
      </>
    );
  }

  if (errorStatuses && !onlyOpenPlaidModeId) {
    return (
      <>
        {highlightedTop && (
          <Box
            height="2px"
            width="calc(100% - 48px)"
            background={{ color: 'danger' }}
            margin={{ bottom: '-2px', horizontal: 'spacing24' }}
            style={{ zIndex: 1 }}
          />
        )}
        <Box
          data-testid="PlaidDirectDepositBanner"
          pad={{
            horizontal: 'spacing24',
            vertical: 'spacing16',
          }}
          background={{ color: 'dangerContainer' }}
          border={{ size: isHighlighted ? '2px' : '1px', color: isHighlighted ? 'danger' : 'dangerBorder' }}
          round="fieldRound"
          direction={withoutButton ? 'column' : 'row'}
          justify="between"
          align={withoutButton ? 'start' : 'center'}
        >
          <Box>
            <Box direction="row" align="center" margin={{ bottom: 'spacing8' }} gap="spacing8">
              <RoundCloseIcon color="danger" />
              <Inscription weight="bold" color="textBody">
                {t('Your bank account could not be verified.')}
              </Inscription>
            </Box>

            <Box>
              <Inscription margin={{ bottom: 'spacing8' }} color="textBody" size="14px">
                {t('Update banking account information to try again or add a new bank account.')}
              </Inscription>
            </Box>
          </Box>
          <Box width="100px" margin={withoutButton ? { top: 'spacing16' } : { left: 'spacing24' }}>
            <AppButton
              onClick={() => {
                if (additionalOnCLickAction) {
                  additionalOnCLickAction();
                }
                openPlaid();
              }}
              testId="plaid-action"
              width="100%"
            >
              {t('Try Again')}
            </AppButton>
          </Box>
          {content}
        </Box>
        {isPlaidLoading && <PlaidModal visible isLoading />}
        <SuccessModal
          header={t('Payment method added successfully!')}
          buttonText={t('Got It!')}
          visible={successModal}
          onSetVisible={() => {
            if (refetch) {
              refetch();
            }
            setSuccessModal(false);
          }}
          buttonStyle={{ width: '200px' }}
        />
        <ErrorModal
          testId="plaid-banner-error-modal"
          visible={errorModal}
          onSetVisible={() => {
            setErrorModal(false);
          }}
          header={t('Something went wrong')}
          helpText={t('Try again later')}
          buttonText={t('Got It!')}
          onSubmit={() => {
            setErrorModal(false);
          }}
        />
      </>
    );
  }

  return (
    <>
      <SuccessModal
        header={t('Payment method added successfully!')}
        buttonText={t('Got It!')}
        visible={successModal}
        onSetVisible={() => {
          if (refetch) {
            refetch();
          }
          setSuccessModal(false);
        }}
        buttonStyle={{ width: '200px' }}
      />
      <ErrorModal
        testId="plaid-banner-error-modal"
        visible={errorModal}
        onSetVisible={() => {
          setErrorModal(false);
        }}
        header={t('Something went wrong')}
        helpText={t('Try again later')}
        buttonText={t('Got It!')}
        onSubmit={() => {
          setErrorModal(false);
        }}
      />
    </>
  );
};
